import { CommonModule } from "@angular/common";
import { Component, HostListener, Inject, Input, LOCALE_ID, OnChanges, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { CloudinaryModule, lazyload, responsive } from "@cloudinary/ng";
import { Cloudinary } from "@cloudinary/url-gen";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { Client, Job, MenuConfig, Page } from "@interfaces";
import { LocalePickerComponent } from "src/app/page-components/locale-picker/locale-picker.component";
import { SubdomainService } from "src/app/rest/subdomain.service";
import { ComponentOverlayComponent } from "../component-overlay/component-overlay.component";

@Component({
    selector: "app-menu",
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, CloudinaryModule, LocalePickerComponent, ComponentOverlayComponent ],
    templateUrl: "./menu.component.html",
})
export class MenuComponent implements OnInit, OnChanges {
    @Input() public entity: Page | Job;
    @Input() public editMode = false;
    @Input() public mobileMode = false;
    public client: Client;
    public closed = true;
    public imagePath: string = "voyse/assets/Voyse_Logotype_LR";
    public logoImg: string;
    public plugins = [responsive(), lazyload()];
    public showHeaderLinks = true;
    public fullWidthLayout = false;
    public narrowWidthLayout = false;
    public logoLink = "/";
    public logoLinkTarget = "_self";
    public keywords = "";
    public menuConfig: MenuConfig = {
        top: [],
        menuHeadings: {},
    };
    public jobLinkParams = {};
    public headerBackground = "";
    constructor(
        private router: Router,
        private subdomain: SubdomainService,
        @Inject(LOCALE_ID) private locale
    ) {}

    ngOnInit() {
        this.subdomain.getClient().subscribe((client) => {
            this.client = client;
            this.setupHeaderLogo();
            this.setMenuConfig();
            this.setupHeaderLinks();
        });
        this.router.events.subscribe((event) => {
            this.closed = true;
        });
    }

    ngOnChanges(): void {
        this.subdomain.getClient().subscribe((client) => {
            this.setupHeaderLogo();
            this.setMenuConfig();
            this.setupHeaderLinks();
        });
    }

    @HostListener("keyup.enter") onEnter() {
        this.quickSearch();
    }

    private setupHeaderLogo() {
        this.imagePath = this.entity?.headerLogo?.public_id || this.client?.logo?.public_id || this.imagePath;
        const cld = new Cloudinary({ cloud: { cloudName: "minirobot" } });
        const img = cld.image(this.imagePath);
        img.resize(fill().height(64));
        this.logoImg = img.toURL();
    }

    private setupHeaderLinks() {

        if(['en', 'en-US'].includes(this.locale)) {
            this.logoLink = this.entity?.linkLogoToWebsite ? this.client?.website : "/";
        } else {
            this.logoLink = this.entity?.linkLogoToWebsite ? this.client?.website : "/" + this.locale;
        }

        this.logoLinkTarget = this.logoLink?.includes('https://') ? "_blank" : "_self";
        this.showHeaderLinks = this.entity?.showHeaderLinks ?? true;
        this.fullWidthLayout = this.entity?.fullWidthLayout;
        this.narrowWidthLayout = this.entity?.headerNarrowWidth;
        this.headerBackground = this.entity?.headerBackground;
    }

    private setMenuConfig() {
        const menuConfig = this.entity?.overrideMenuConfig ? this.entity?.menuConfig : this.client?.menuConfig;
        if(!menuConfig) return;
        this.menuConfig.top = menuConfig.top
            .filter((h) => h.show)
            .map((h) => {
                // Split hash fragment from slug if it exists
                let slug = h.slug;
                let hashFragment = '';
                if (slug?.includes('#')) {
                    [slug, hashFragment] = slug.split('#');
                }
                return {
                    ...h,
                    slug,
                    hashFragment,
                    title: this.locale && h.additionalTitles?.[this.locale] ? h.additionalTitles?.[this.locale] : h.title,
                };
            });

        // Handle sub-items
        this.menuConfig.top.forEach(item => {
            if (item.subItems) {
                item.subItems = item.subItems
                    .filter(h => h.show)
                    .map(h => {
                        let slug = h.slug;
                        let hashFragment = '';
                        if (slug?.includes('#')) {
                            [slug, hashFragment] = slug.split('#');
                        }
                        return {
                            ...h,
                            slug,
                            hashFragment,
                            title: this.locale && h.additionalTitles?.[this.locale] ? h.additionalTitles?.[this.locale] : h.title,
                        };
                    });
            }
        });

        Object.keys(menuConfig.menuHeadings || {}).forEach((key) => {
            this.menuConfig.menuHeadings[key] = {
                ...this.client.menuConfig.menuHeadings[key],
                title:
                    this.locale && this.menuConfig.menuHeadings[key]?.additionalTitles?.[this.locale]
                        ? this.client.menuConfig.menuHeadings[key].additionalTitles?.[this.locale]
                        : this.client.menuConfig.menuHeadings[key].title,
            };
            this.menuConfig.menuHeadings[key].links = [...menuConfig.menuHeadings[key].links]
                .filter((h) => h.show)
                .map(h => {
                    let slug = h.slug;
                    let hashFragment = '';
                    if (slug?.includes('#')) {
                        [slug, hashFragment] = slug.split('#');
                    }
                    return {
                        ...h,
                        slug,
                        hashFragment,
                        title: this.locale && h.additionalTitles?.[this.locale] ? h.additionalTitles?.[this.locale] : h.title,
                    };
                });
        });
    }

    public toggleMenu() {
        this.closed = !this.closed;
    }

    public quickSearch() {
        this.router.navigate([this.entity.quickSearch || '/jobs'], { queryParams: { keywords: this.keywords } });
    }

    public navigateWithHash(slug: string, hashFragment?: string) {
        if (hashFragment) {
            this.toggleMenu();
            // If we're already on the page, just scroll to the section
            if (this.router.url.split('#')[0] === `/${slug}`) {
                this.scrollToSection(hashFragment);
            } else {
                // Navigate to the page and then scroll to the section
                this.router.navigate([`/${slug}`]).then(() => {
                    setTimeout(() => this.scrollToSection(hashFragment), 100);
                });
            }
        } else {
            // Regular navigation without hash
            this.router.navigate([`/${slug}`]);
        }
    }

    private scrollToSection(sectionId: string) {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }
}
